import React from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, FormGroup, IconButton, Link, Paper, Stack, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { get, del, post, patch } from '../utils/api';
import PageLayout from '../components/common/PageLayout';

const STATUS_COLOR = { scheduled: 'info', sending: 'warning', sent: 'success', cancelled: 'default', failed: 'error' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDateTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function dayKey(iso) { try { return new Date(iso).toISOString().slice(0, 10); } catch { return String(iso).slice(0, 10); } }
function fmtDayHeader(key) {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = dt.toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' });
  return `${wd}, ${MONTHS[m - 1]} ${d}, ${y}`;
}

export default function ScheduledEmailsPage() {
  const [schedules, setSchedules] = React.useState([]);
  const [listMeta, setListMeta] = React.useState({});
  const [view, setView] = React.useState('list');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [digest, setDigest] = React.useState(null); // { send_enabled, list_ids, events }
  const [digestBusy, setDigestBusy] = React.useState('');
  const [digestHistory, setDigestHistory] = React.useState([]); // recent sends
  const [archiveOpen, setArchiveOpen] = React.useState(false);
  const [digestMsg, setDigestMsg] = React.useState(null); // inline result for digest actions, shown next to the buttons
  const [bulletinDlg, setBulletinDlg] = React.useState(null); // { title, html } — in-app bulletin preview dialog
  const [testEmail, setTestEmail] = React.useState('');
  const [savingCfg, setSavingCfg] = React.useState(false);

  // Prefill the test-email field with the signed-in admin's email once the digest loads (editable after).
  React.useEffect(() => { if (digest?.user_email && !testEmail) setTestEmail(digest.user_email); }, [digest?.user_email, testEmail]);

  // Persist a config change (auto-send toggle and/or selected list ids), then refresh the box.
  const saveCfg = async (patchBody) => {
    setSavingCfg(true); setDigestMsg(null);
    try { await patch('/api/manage/weekly-digest/config', patchBody); await loadDigest(); }
    catch (e) { setDigestMsg({ severity: 'error', text: e?.message || 'Could not save setting' }); }
    finally { setSavingCfg(false); }
  };
  const toggleList = (id, checked) => {
    const cur = (digest?.list_ids || []).map(String);
    const next = checked ? [...new Set([...cur, String(id)])] : cur.filter((x) => x !== String(id));
    saveCfg({ list_ids: next });
  };
  const toggleSend = (enabled) => {
    if (enabled && !window.confirm('Turn ON auto-send? The digest will email the selected list(s) every Friday 9 AM Central.')) return;
    saveCfg({ send_enabled: enabled });
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); setOk(`Copied: ${url}`); }
    catch { setError('Could not copy — select and copy manually.'); }
  };

  const loadDigest = React.useCallback(async () => {
    try { const d = await get('/api/manage/weekly-digest/preview'); setDigest(d); } catch { setDigest(null); }
  }, []);

  const loadDigestHistory = React.useCallback(async () => {
    try { const d = await get('/api/manage/weekly-digest/history'); setDigestHistory(Array.isArray(d?.sends) ? d.sends : []); }
    catch { setDigestHistory([]); }
  }, []);

  // View a PAST send. Sent issues are published — open their public archive page (/weekly/<id>) on the
  // events.* origin so the email images load. Failed sends aren't published, so fall back to the stored
  // HTML written inline (auth travels with the request; images may be blocked by the admin-origin CSP).
  const viewSentDigest = async (id, status) => {
    setError('');
    const base = String(digest?.weekly_url || '').replace(/\/weekly$/, '');
    if (status === 'sent' && base) { window.open(`${base}/weekly/${id}`, '_blank', 'noopener'); return; }
    try {
      const d = await get(`/api/manage/weekly-digest/history/${id}?format=json`);
      const w = window.open('', '_blank');
      if (w) { w.document.open(); w.document.write(d.html); w.document.close(); }
      else setError('Popup blocked — allow popups to view.');
    } catch (e) { setError(e?.message || 'Failed to open sent digest'); }
  };

  // Preview: open the live public bulletin (/weekly) in a new tab. It's the same render as a send, served
  // as a real page on the events.* origin — which loads the email images. (A document.write'd about:blank
  // tab inherits the admin origin's CSP and blocks those cross-origin images, so they showed as broken.)
  const previewDigest = async () => {
    setDigestBusy('preview'); setDigestMsg(null);
    try {
      const url = digest?.weekly_url || (await get('/api/manage/weekly-digest/preview'))?.weekly_url;
      if (url) window.open(url, '_blank', 'noopener');
      else setDigestMsg({ severity: 'error', text: 'Preview URL unavailable — reload and try again.' });
    } catch (e) { setDigestMsg({ severity: 'error', text: e?.message || 'Preview failed' }); } finally { setDigestBusy(''); }
  };

  const testDigest = async () => {
    const to = String(testEmail || '').trim();
    if (!to || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) { setDigestMsg({ severity: 'error', text: 'Enter a valid test email address.' }); return; }
    setDigestBusy('test'); setDigestMsg(null);
    try {
      const r = await post('/api/manage/weekly-digest/test', { email: to });
      setDigestMsg({ severity: 'success', text: `Test digest sent to ${r?.sent_to} (${r?.events} event(s)).` });
    } catch (e) { setDigestMsg({ severity: 'error', text: e?.message || 'Test send failed' }); } finally { setDigestBusy(''); }
  };

  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await get('/api/manage/email-schedules');
      const arr = Array.isArray(res?.schedules) ? res.schedules : [];
      setSchedules(arr);
      const ids = [...new Set(arr.flatMap((s) => (Array.isArray(s.list_ids) ? s.list_ids.map(String) : [])))];
      if (ids.length) {
        try {
          const lm = await get(`/api/manage/getresponse-lists?ids=${encodeURIComponent(ids.join(','))}`);
          const map = {};
          (lm?.lists || []).forEach((l) => { map[String(l.id)] = l; });
          setListMeta(map);
        } catch { /* fall back to raw ids */ }
      }
    } catch (e) { setError(e?.message || 'Failed to load scheduled emails'); }
    finally { setLoading(false); }
  }, []);

  const listLabel = React.useCallback((id) => {
    const m = listMeta[String(id)];
    if (!m) return String(id);
    return m.count != null ? `${m.name} (${m.count})` : m.name;
  }, [listMeta]);
  const renderLists = React.useCallback((ids) => (Array.isArray(ids) ? ids : []).map(listLabel).join(', '), [listLabel]);

  React.useEffect(() => { load(); loadDigest(); loadDigestHistory(); }, [load, loadDigest, loadDigestHistory]);

  const onCancel = async (id) => {
    setError(''); setOk('');
    try { await del(`/api/manage/email-schedule/${id}`); setOk('Schedule cancelled.'); await load(); }
    catch (e) { setError(e?.message || 'Failed to cancel'); }
  };

  const byDay = React.useMemo(() => {
    const map = new Map();
    schedules.forEach((s) => {
      const k = dayKey(s.send_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [schedules]);

  const counts = React.useMemo(() => {
    const c = { scheduled: 0, sent: 0, failed: 0, cancelled: 0, sending: 0 };
    schedules.forEach((s) => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [schedules]);

  const row = (s) => (
    <TableRow key={s.id} hover>
      <TableCell>{s.event_title || s.event_key}</TableCell>
      <TableCell>{s.is_pub_review ? 'Publication review' : s.is_send_now ? 'Send now' : `${s.days_before} day(s) before`}</TableCell>
      <TableCell>{fmtDateTime(s.send_at)}</TableCell>
      <TableCell>{renderLists(s.list_ids)}</TableCell>
      <TableCell><Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} variant="outlined" /></TableCell>
      <TableCell sx={{ maxWidth: 240, color: 'error.main', fontSize: 12 }}>{s.error_text || (s.sent_at ? `sent ${fmtDateTime(s.sent_at)}` : '')}</TableCell>
      <TableCell align="right">
        {s.status === 'scheduled' ? (
          <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => onCancel(s.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        ) : null}
      </TableCell>
    </TableRow>
  );

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Scheduled Event Emails</Typography>
          <Typography variant="body2" color="text.secondary">
            All calendar-event email schedules. Set them up per event from Manage → Calendar Events → Email.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="list">List</ToggleButton>
            <ToggleButton value="calendar">Calendar</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh"><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></Tooltip>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Weekly Digest</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Auto-sends every <strong>Friday 9:00 AM Central</strong> — upcoming events (next 4 weeks) + the weekly puja schedule.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap">
              <Chip size="small" variant="outlined" label={digest ? `${digest.events} event(s) this cycle` : 'loading…'} />
              <Chip size="small" variant="outlined" label={`Lists: ${(digest?.list_ids || []).join(', ') || '—'}`} />
              <Chip size="small" color={digest?.send_enabled ? 'success' : 'default'} variant={digest?.send_enabled ? 'filled' : 'outlined'}
                label={digest?.send_enabled ? 'Auto-send ON' : 'Auto-send OFF (safe)'} />
            </Stack>
          </Box>
          <Stack spacing={0.75} alignItems="flex-end">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
              <Button size="small" variant="outlined" onClick={previewDigest} disabled={digestBusy === 'preview'}>
                {digestBusy === 'preview' ? 'Opening…' : 'Preview'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setArchiveOpen(true)}>
                Archived{digestHistory.length ? ` (${digestHistory.length})` : ''}
              </Button>
            </Stack>
            {/* Result of the Preview/Test actions, shown right under the buttons so it's easy to see. */}
            {digestMsg ? (
              <Alert severity={digestMsg.severity} onClose={() => setDigestMsg(null)}
                sx={{ py: 0, width: { xs: '100%', sm: 'auto' } }}>
                {digestMsg.text}
              </Alert>
            ) : null}
          </Stack>
        </Stack>

        {/* Settings + sharing — auto-send toggle, mailing lists, a test send, and the public links. */}
        {digest?.weekly_url && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {/* Auto-send toggle — governs the real Friday cron send. */}
            <FormControlLabel
              control={<Switch checked={!!digest?.send_enabled} disabled={savingCfg} onChange={(e) => toggleSend(e.target.checked)} />}
              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>
                Weekly auto-send — {digest?.send_enabled ? <Box component="span" sx={{ color: 'success.main' }}>ON (emails every Friday 9 AM)</Box> : <Box component="span" sx={{ color: 'text.secondary' }}>OFF (safe — nothing is sent)</Box>}
              </Typography>}
            />

            {/* Mailing lists to send to — checkboxes from the allowlist. */}
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mt: 1 }}>Mailing lists</Typography>
            {Array.isArray(digest?.lists) && digest.lists.length ? (
              <FormGroup>
                {digest.lists.map((l) => (
                  <FormControlLabel key={l.id} sx={{ my: -0.5 }}
                    control={<Checkbox size="small" disabled={savingCfg}
                      checked={(digest?.list_ids || []).map(String).includes(String(l.id))}
                      onChange={(e) => toggleList(l.id, e.target.checked)} />}
                    label={<Typography variant="body2">{l.name || l.id}{l.count != null ? <Box component="span" sx={{ color: 'text.secondary' }}> · {l.count} subscribers</Box> : null}{l.is_default ? <Chip size="small" label="default" sx={{ ml: 0.5, height: 16, fontSize: 10 }} /> : null}</Typography>} />
                ))}
              </FormGroup>
            ) : (
              <Typography variant="caption" color="text.secondary">No selectable lists configured.</Typography>
            )}

            <Divider sx={{ my: 1.25 }} />

            {/* Send a test — prefilled with your email, editable. Never touches the real lists. */}
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>Send a test copy</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
              <TextField size="small" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com" sx={{ minWidth: 240, bgcolor: 'background.paper' }} />
              <Button size="small" variant="contained" onClick={testDigest} disabled={digestBusy === 'test'}>
                {digestBusy === 'test' ? 'Sending…' : 'Send test'}
              </Button>
            </Stack>

            {/* Public links — clickable; open the bulletin in an in-app dialog (or copy to share). */}
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.75 }}>
              Public links — copy &amp; share anywhere (no login needed)
            </Typography>
            {[
              { url: digest.weekly_url, label: 'Current bulletin', note: 'always shows the current bulletin', onOpen: () => setBulletinDlg({ title: 'Current bulletin', html: digest.html }) },
              { url: digest.archive_url, label: 'All past issues', note: 'all past issues', onOpen: () => setArchiveOpen(true) },
            ].map((l) => (
              <Stack key={l.url} direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                <Link component="button" type="button" onClick={l.onOpen} variant="caption"
                  sx={{ fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>{l.label}</Link>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, minWidth: 160, wordBreak: 'break-all', color: 'text.secondary' }}>{l.url}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>— {l.note}</Typography>
                <Tooltip title="Copy link"><IconButton size="small" onClick={() => copyLink(l.url)}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                <Tooltip title="Open in new tab"><IconButton size="small" component="a" href={l.url} target="_blank" rel="noreferrer"><OpenInNewIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              </Stack>
            ))}
          </Box>
        )}
      </Paper>

      {/* Archived past sends — dialog opened by the "Archived" button. */}
      <Dialog open={archiveOpen} onClose={() => setArchiveOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Weekly Digest — Past Sends</DialogTitle>
        <DialogContent dividers>
          {digestHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No sends yet. The Friday cron (or a manual send) will list issues here — each with a “View” link to exactly what went out.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Sent</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Events</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {digestHistory.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(s.sent_at)}</TableCell>
                    <TableCell><Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} variant="outlined" /></TableCell>
                    <TableCell>
                      {s.subject || '(no subject)'}
                      {s.error ? <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>{s.error}</Typography> : null}
                    </TableCell>
                    <TableCell align="center">{s.event_count ?? '—'}</TableCell>
                    <TableCell align="right">
                      {s.has_html
                        ? <Button size="small" variant="text" onClick={() => viewSentDigest(s.id, s.status)}>View</Button>
                        : <Typography variant="caption" color="text.disabled">no snapshot</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          {digest?.archive_url && <Button component="a" href={digest.archive_url} target="_blank" rel="noreferrer" startIcon={<OpenInNewIcon />}>Public archive page</Button>}
          <Button onClick={() => setArchiveOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* In-app bulletin preview — renders the digest HTML in a sandboxed iframe (images are allowed by
          the app's img-src CSP, so the logo + thumbnails load here). */}
      <Dialog open={!!bulletinDlg} onClose={() => setBulletinDlg(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {bulletinDlg?.title || 'Bulletin'}
          {digest?.weekly_url && <Button size="small" component="a" href={digest.weekly_url} target="_blank" rel="noreferrer" startIcon={<OpenInNewIcon />}>Open page</Button>}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box component="iframe" title="Bulletin preview" srcDoc={bulletinDlg?.html || ''} sandbox="allow-popups allow-same-origin"
            sx={{ width: '100%', height: '72vh', border: 0, display: 'block', bgcolor: '#fff' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulletinDlg(null)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip label={`Scheduled: ${counts.scheduled || 0}`} color="info" variant="outlined" />
        <Chip label={`Sent: ${counts.sent || 0}`} color="success" variant="outlined" />
        <Chip label={`Failed: ${counts.failed || 0}`} color="error" variant="outlined" />
        <Chip label={`Cancelled: ${counts.cancelled || 0}`} variant="outlined" />
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {ok ? <Alert severity="success" sx={{ mb: 1.5 }}>{ok}</Alert> : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : schedules.length === 0 ? (
        <Paper sx={{ p: 3 }}><Typography>No scheduled emails yet.</Typography></Paper>
      ) : view === 'list' ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Send at</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>List (recipients)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>{schedules.map(row)}</TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Stack spacing={2}>
          {byDay.map(([key, items]) => (
            <Paper key={key} sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>{fmtDayHeader(key)}</Typography>
              <Stack spacing={0.5}>
                {items.map((s) => (
                  <Stack key={s.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, borderBottom: '1px solid #eee' }}>
                    <Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} variant="outlined" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(s.send_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>{s.event_title || s.event_key} {s.is_send_now ? '(send now)' : `(${s.days_before}d before)`}</Typography>
                    {s.status === 'scheduled' ? (
                      <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => onCancel(s.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </PageLayout>
  );
}
